/**
 * Whitelist validation and sanitization for user-facing text fields (XSS mitigation).
 */

const DANGEROUS_MARKUP_PATTERN = /<[^>]*>|&lt;|&gt;|&#x?[0-9a-f]+;|javascript\s*:|on\w+\s*=/i;
const DANGEROUS_CHARS_PATTERN = /[<>'"&]/;

export const BROADCAST_CHANNEL_LIMITS = {
    name: {
        max: 100,
        pattern: /^[a-zA-Z0-9\s\-_.]+$/,
        label: 'Channel name',
    },
    description: {
        max: 500,
        pattern: /^[a-zA-Z0-9\s\-_.,()]+$/,
        label: 'Channel description',
    },
};

/**
 * Validates broadcast channel name or description against a safe character whitelist.
 * @param {'name' | 'description'} field
 * @param {string} value
 * @returns {{ valid: boolean, message?: string, sanitized?: string }}
 */
export const validateBroadcastChannelField = (field, value) => {
    const limits = BROADCAST_CHANNEL_LIMITS[field];
    const trimmed = (value ?? '').trim();

    if (!trimmed) {
        return { valid: false, message: `${limits.label} is required.` };
    }

    if (trimmed.length > limits.max) {
        return { valid: false, message: `${limits.label} must be at most ${limits.max} characters.` };
    }

    if (DANGEROUS_MARKUP_PATTERN.test(trimmed) || DANGEROUS_CHARS_PATTERN.test(trimmed)) {
        return {
            valid: false,
            message: `${limits.label} contains invalid characters. HTML tags and symbols (<, >, &, quotes) are not allowed.`,
        };
    }

    if (!limits.pattern.test(trimmed)) {
        return {
            valid: false,
            message: `${limits.label} may only contain letters, numbers, spaces, and basic punctuation.`,
        };
    }

    return { valid: true, sanitized: trimmed };
};

/**
 * Validates both broadcast channel fields for form submit / API calls.
 * @param {{ name: string, description: string }} data
 * @returns {{ valid: boolean, message?: string, sanitized?: { name: string, description: string } }}
 */
export const validateBroadcastChannelPayload = (data) => {
    const nameResult = validateBroadcastChannelField('name', data?.name);
    if (!nameResult.valid) {
        return nameResult;
    }

    const descriptionResult = validateBroadcastChannelField('description', data?.description);
    if (!descriptionResult.valid) {
        return descriptionResult;
    }

    return {
        valid: true,
        sanitized: {
            name: nameResult.sanitized,
            description: descriptionResult.sanitized,
        },
    };
};

/**
 * Strips markup and dangerous characters from stored values before display.
 * @param {unknown} value
 * @returns {string}
 */
export const sanitizeForDisplay = (value) => {
    if (value == null) {
        return '';
    }

    return String(value)
        .replace(/<[^>]*>/gi, '')
        .replace(/[<>'"&]/g, '')
        .replace(/javascript\s*:/gi, '')
        .replace(/on\w+\s*=/gi, '');
};
